import Calculator, {
  Regions,
  PortfolioComparisonItem,
  UsYTDTaxData,
  CaYTDTaxData,
  UsTransaction,
  CaTransaction,
  UsComparisonTaxResult,
  CaComparisonTaxResult,
  UsTaxRateConfiguration,
  CaTaxRateConfiguration,
  TaxRegionKey,
} from '@capintel/instruments-lib';

import AppRegion from '@/types/app-region';
import { RegionTaxInputs, UsTaxAnalysisInputValues, CaTaxAnalysisInputValues, TaxAnalysisInputValues } from '../useTaxAnalysisInputs/useTaxAnalysisInputs';

/**
 * Tax calculation configuration for a specific region
 */
export type UsTaxCalculationConfig = { region: Regions.US; taxRates: UsTaxRateConfiguration; ytdData: UsYTDTaxData };
export type CaTaxCalculationConfig = { region: Regions.CA; taxRates: CaTaxRateConfiguration; ytdData: CaYTDTaxData };
export type TaxCalculationConfig = UsTaxCalculationConfig | CaTaxCalculationConfig;

/**
 * Builds the US tax calculation configuration
 */
const getUsTaxCalculationConfig = (taxInputs: UsTaxAnalysisInputValues): TaxCalculationConfig | null => {
  const shortTermRate = taxInputs.shortTermCapitalGainsRate;
  const longTermRate = taxInputs.longTermCapitalGainsRate;
  if (shortTermRate == null || longTermRate == null) return null;

  return {
    region: Regions.US,
    taxRates: {
      shortTermCapitalGainsTaxRate: shortTermRate / 100,
      longTermCapitalGainsTaxRate: longTermRate / 100,
    },
    ytdData: {
      shortTermYTDGain: taxInputs.shortTermYTDGain ?? 0,
      shortTermYTDLoss: taxInputs.shortTermYTDLoss ?? 0,
      longTermYTDGain: taxInputs.longTermYTDGain ?? 0,
      longTermYTDLoss: taxInputs.longTermYTDLoss ?? 0,
      shortTermCarriedForwardLoss: taxInputs.shortTermCarriedForwardLoss ?? 0,
      longTermCarriedForwardLoss: taxInputs.longTermCarriedForwardLoss ?? 0,
    },
  };
};

/**
 * Builds the CA tax calculation configuration
 */
const getCaTaxCalculationConfig = (taxInputs: CaTaxAnalysisInputValues): TaxCalculationConfig | null => {
  const marginalRate = taxInputs.marginalTaxRate;
  if (marginalRate == null) return null;

  return {
    region: Regions.CA,
    taxRates: {
      marginalIncomeTaxRate: marginalRate / 100,
    },
    ytdData: {
      ytdGain: taxInputs.ytdGain ?? 0,
      ytdLoss: taxInputs.ytdLoss ?? 0,
      carryForwardLoss: taxInputs.carryForwardLoss ?? 0,
    },
  };
};

const taxConfigBuilders = {
  [TaxRegionKey.US]: (values: TaxAnalysisInputValues) => getUsTaxCalculationConfig(values as UsTaxAnalysisInputValues),
  [TaxRegionKey.CA]: (values: TaxAnalysisInputValues) => getCaTaxCalculationConfig(values as CaTaxAnalysisInputValues),
};

/**
 * Builds the tax calculation configuration based on region inputs
 */
export const getTaxCalculationConfig = (regionInputs: RegionTaxInputs): TaxCalculationConfig | null => {
  if (!regionInputs) {
    return null;
  }
  return taxConfigBuilders[regionInputs.region](regionInputs.values);
};

/**
 * Executes tax calculation using the calculator library
 */
export function calculateTax(
  currentPortfolio: PortfolioComparisonItem,
  targetPortfolio: PortfolioComparisonItem | undefined,
  config: UsTaxCalculationConfig,
): UsComparisonTaxResult;
export function calculateTax(
  currentPortfolio: PortfolioComparisonItem,
  targetPortfolio: PortfolioComparisonItem | undefined,
  config: CaTaxCalculationConfig,
): CaComparisonTaxResult;
export function calculateTax(
  currentPortfolio: PortfolioComparisonItem,
  targetPortfolio: PortfolioComparisonItem | undefined,
  config: TaxCalculationConfig,
): UsComparisonTaxResult | CaComparisonTaxResult;
export function calculateTax(
  currentPortfolio: PortfolioComparisonItem,
  targetPortfolio: PortfolioComparisonItem | undefined,
  config: TaxCalculationConfig,
): UsComparisonTaxResult | CaComparisonTaxResult | undefined {
  const calculator = Calculator.getInstance().portfolio();

  const regionCalculators = {
    [Regions.US]: () =>
      calculator
        .calculateTransitionTaxes<Regions.US>(currentPortfolio, targetPortfolio, Regions.US, config.taxRates as UsTaxRateConfiguration, new Date())
        .withTaxImplications(config.ytdData as UsYTDTaxData)
        .calculate(),
    [Regions.CA]: () =>
      calculator
        .calculateTransitionTaxes<Regions.CA>(currentPortfolio, targetPortfolio, Regions.CA, config.taxRates as CaTaxRateConfiguration, new Date())
        .withTaxImplications(config.ytdData as CaYTDTaxData)
        .calculate(),
  };

  return regionCalculators[config.region]();
}

/**
 * Maps a US transaction to holding tax metrics
 * Schema expects US-specific fields nested under 'US' key, plus deprecated root-level fields
 */
export const mapUsTransactionToHoldingTaxMetrics = (transaction: UsTransaction) => {
  const shortTermUnrealizedGainLoss = transaction.unrealizedGainOrLoss?.SHORT_TERM ?? 0;
  const longTermUnrealizedGainLoss = transaction.unrealizedGainOrLoss?.LONG_TERM ?? 0;
  const totalUnrealizedGainLoss = shortTermUnrealizedGainLoss + longTermUnrealizedGainLoss;

  return {
    holdingId: transaction.holdingId,
    taxMetrics: {
      marketValue: transaction.marketValue,
      currentAllocation: transaction.currentAllocation,
      valueChange: transaction.valueChange,
      estimatedRealizedGainLoss: transaction.estimatedRealizedGainLoss,
      taxCostToReachTarget: transaction.taxCostToGetToTargetAllocation,
      // US-specific fields nested under region key
      [AppRegion.US]: {
        shortTermUnrealizedGainLoss,
        longTermUnrealizedGainLoss,
        totalUnrealizedGainLoss,
      },
    },
    lots: transaction.lotsWithTaxMetrics?.length ? transaction.lotsWithTaxMetrics : undefined,
  };
};

/**
 * Maps a CA transaction to holding tax metrics
 * Schema expects CA-specific fields nested under 'CAN' key
 * Note: CA doesn't support lots
 */
export const mapCaTransactionToHoldingTaxMetrics = (transaction: CaTransaction) => ({
  holdingId: transaction.holdingId,
  taxMetrics: {
    marketValue: transaction.marketValue,
    currentAllocation: transaction.currentAllocation,
    valueChange: transaction.valueChange,
    estimatedRealizedGainLoss: transaction.estimatedRealizedGainLoss,
    taxCostToReachTarget: transaction.taxCostToGetToTargetAllocation,
    // CA-specific fields nested under region key
    [AppRegion.CAN]: {
      unrealizedGainLoss: transaction.unrealizedGainOrLoss?.REGULAR ?? 0,
    },
  },
});
